import React, { useCallback } from 'react';
import { ILoan, IUnitLoan } from '../../../types/loan';
import RoundedBox from '../../../components/Containers/RoundedBox';
import { BATE_RED_HEADING_STYLE } from '../../../common/utils/constants';
import { getLoanById } from '../../../api';
import { useAppContext } from '../../../context/AppContext';
import { useFetchApiData } from '../../../utils/hooks';
import { formatDate } from '../../../utils/date';
import { formatCurrency } from '../../../utils/numbers';

interface Props {
  loan: ILoan;
  unitLoan?: IUnitLoan;
}

const LoadingIndicator: React.FC<{loading: boolean}> = ({ loading }) => {
  return <span className={`${loading ? 'animate-pulse h-6 w-16 bg-gray-200 rounded' : 'hidden'}`} />;
};

const LoanCard: React.FC<Props> = ({ loan, unitLoan }) => {
  const { currentResidence } = useAppContext();

  const fetchLoan = useCallback(async () => {
    if (currentResidence?.clientId != null && loan.loanIndexNr != null) {
      return getLoanById(currentResidence.clientId, loan.loanIndexNr);
    }
    return null;
  }, [currentResidence, loan]);

  const {
    data: loanDetails,
    loading: loadingLoanDetails,
  } = useFetchApiData({ fetch: fetchLoan, identifier: 'LoanCard:loanDetails' });

  return (
    <RoundedBox>
      <div>
        <h2 className={BATE_RED_HEADING_STYLE}>
          {loan.supplierFullName}
          <span className={loan.IsIn ? undefined : 'hidden'}>
            : med IN-ordning
          </span>
        </h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 mt-8">
        <div className="flex flex-col row-span-2 gap-6">
          <div className="flex flex-col">
            <span>Andel fellesgjeld</span>
            <span className="text-bate-red font-clanot-black">
              {formatCurrency(unitLoan?.loanAmount)}
            </span>
          </div>
          <div className="flex flex-col">
            <span>{`Fellesgjeld for ${currentResidence?.clientName}`}</span>
            <span className="text-bate-red font-clanot-black">
              {formatCurrency(loan.loanBalance)}
            </span>
          </div>
        </div>
        <div className="flex flex-col">
          <span>Siste renteendring</span>
          <LoadingIndicator loading={loadingLoanDetails} />
          <span className={`font-bold ${loadingLoanDetails ? 'hidden' : ''}`}>
            {loanDetails?.interestRateChangeDate
              ? formatDate(loanDetails.interestRateChangeDate)
              : '-'}
          </span>
        </div>
        <div className="flex flex-col">
          <span>Rentesats</span>
          <span className="font-bold">{loan.interestRate}</span>
        </div>
        <div className="flex flex-col">
          <span>Neste renteendring</span>
          <LoadingIndicator loading={loadingLoanDetails} />
          <span className={`font-bold ${loadingLoanDetails ? 'hidden' : ''}`}>
            {loanDetails?.nextInterestRateChangeDate
              ? formatDate(loanDetails?.nextInterestRateChangeDate)
              : '-'}
          </span>
        </div>
        <div className="flex flex-col">
          <span>Ny rentesats</span>
          <LoadingIndicator loading={loadingLoanDetails} />
          <span className={`font-bold ${loadingLoanDetails ? 'hidden' : ''}`}>
            {loanDetails?.nextInterestRate ?? '-'}
          </span>
        </div>
        <div className="flex flex-col">
          <span>Antall terminer</span>
          <LoadingIndicator loading={loadingLoanDetails} />
          <span className={`font-bold ${loadingLoanDetails ? 'hidden' : ''}`}>
            {loanDetails?.termsPerYear}
          </span>
        </div>
        <div className="flex flex-col">
          <span>Siste termin</span>
          <LoadingIndicator loading={loadingLoanDetails} />
          <span className={`font-bold ${loadingLoanDetails ? 'hidden' : ''}`}>
            {loanDetails?.lastRepaymentDate
              ? formatDate(loanDetails?.lastRepaymentDate)
              : '-'}
          </span>
        </div>
        <div className="flex flex-col">
          <span>Beløp på obligasjon</span>
          <LoadingIndicator loading={loadingLoanDetails} />
          <span className={`font-bold ${loadingLoanDetails ? 'hidden' : ''}`}>
            {formatCurrency(loanDetails?.bondAmount)}
          </span>
        </div>
        <div className="flex flex-col">
          <span>Dato for låneopptak</span>
          <LoadingIndicator loading={loadingLoanDetails} />
          <span className={`font-bold ${loadingLoanDetails ? 'hidden' : ''}`}>
            {loanDetails?.startDate
              ? formatDate(loanDetails?.startDate)
              : '-'}
          </span>
        </div>
      </div>
    </RoundedBox>
  );
};

export default LoanCard;
